/*
  alz-motion.js
  Alyzen Paris -- moteur animations GSAP + ScrollTrigger
  Version : 2026-06-11 v2 (richesse elevee -- niveau Aesop/Typology)

  Charge APRES gsap.min.js + ScrollTrigger.min.js (defer, dans theme.liquid)
  REGLES :
    - prefers-reduced-motion : aucune animation, etat final direct, sortie immediate
    - Mobile ET desktop : reveals + slow-zoom actifs sur les deux
    - Seul le parallax lourd (formes SVG hero) reste desktop-only (> 750px)
    - Anti-FOUC : .alz-js-ready pose sur <html> avant le premier gsap.set()
    - ASCII pur
    - ScrollTrigger pin + scrub sur alz-ritual si >= 750px, fallback colonne mobile
*/

(function () {
  'use strict';

  /* -------------------------------------------------------------------------
     0. GUARD -- reduced-motion : sortie immediate, tout reste visible
     Pour la video hero : supprimer autoplay + pause apres chargement.
     Le CSS masque deja .alz-hero__video via display:none ; on retire aussi
     autoplay pour eviter que certains navigateurs jouent la video en fond.
     L'image .alz-hero__poster est rendue visible via JS egalement
     (redondant avec CSS, mais securise le cas ou le navigateur ignore le CSS).
  --------------------------------------------------------------------------- */
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    document.documentElement.classList.add('alz-js-ready');
    /* Stopper toutes les videos hero et afficher le poster statique */
    (function handleReducedVideos() {
      function pauseHeroVideos() {
        var videos = document.querySelectorAll('[data-alz-video-hero]');
        videos.forEach(function (v) {
          v.removeAttribute('autoplay');
          v.pause();
          v.style.display = 'none';
          /* Rendre visible le poster statique adjacent.
             La classe visee ici etait .alz-hero__video-poster-css, qui
             n'existe plus depuis la refonte du hero du 2026-08-23 : ce code
             ne trouvait donc rien. Le poster s'appelle .alz-hero__poster et
             il est desormais toujours affiche sans JS, mais on retablit le
             bon selecteur pour les navigateurs qui ignoreraient le CSS. */
          var poster = v.parentNode && v.parentNode.querySelector('.alz-hero__poster');
          if (poster) {
            poster.style.display = 'block';
          }
        });
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', pauseHeroVideos);
      } else {
        pauseHeroVideos();
      }
    }());
    return;
  }

  /* -------------------------------------------------------------------------
     1. VERIFICATION GSAP disponible
  --------------------------------------------------------------------------- */
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn('[alz-motion] GSAP ou ScrollTrigger non disponible.');
    document.documentElement.classList.add('alz-js-ready');
    return;
  }

  /* -------------------------------------------------------------------------
     2. ENREGISTREMENT SCROLLTRIGGER
  --------------------------------------------------------------------------- */
  gsap.registerPlugin(ScrollTrigger);

  /* Marque JS pret -- elements pourront recevoir will-change */
  document.documentElement.classList.add('alz-js-ready');

  /* -------------------------------------------------------------------------
     2 bis. DEFILEMENT FLUIDE (moteur Allure, formule Essentiel)

     La roue de la souris ne saute plus d'un cran a l'autre : la page glisse
     et s'arrete en douceur. C'est l'effet le plus reconnaissable d'Allure, et
     le theme partageait deja GSAP et ScrollTrigger avec lui : Lenis etait la
     seule piece manquante, 16 Ko.

     Trois precautions :
     - il ne demarre qu'apres le premier affichage, pour ne rien couter au
       chargement que l'on vient tout juste de reparer ;
     - il est branche sur l'horloge de GSAP, sinon les deux se disputent la
       meme image et le defilement tremble ;
     - il ne demarre pas du tout sur ecran tactile (le geste du doigt a deja
       son inertie native, en rajouter donne une sensation de flottement) ni
       si l'utilisateur a demande moins d'animation.
  --------------------------------------------------------------------------- */
  function demarrerDefilementFluide() {
    if (typeof Lenis === 'undefined') return;
    if (window.matchMedia('(hover: none)').matches) return;

    var lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }

  if (document.readyState === 'complete') {
    demarrerDefilementFluide();
  } else {
    window.addEventListener('load', demarrerDefilementFluide, { once: true });
  }

  /* -------------------------------------------------------------------------
     3. CONSTANTES TIMING PREMIUM
  --------------------------------------------------------------------------- */
  var T = {
    fast:      0.8,
    mid:       1.0,
    slow:      1.2,
    xslow:     1.4,
    ease1:     'power3.out',
    ease2:     'expo.out',
    stagger:   0.08,
    staggerMd: 0.12
  };

  /* -------------------------------------------------------------------------
     4. CURSEUR SOIGNE (pointer: fine = souris/trackpad, pas touch)
  --------------------------------------------------------------------------- */
  function initCursor() {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var cursor = document.createElement('div');
    cursor.className = 'alz-cursor';
    document.body.appendChild(cursor);

    var mx = window.innerWidth / 2;
    var my = window.innerHeight / 2;
    var cx = mx;
    var cy = my;

    document.addEventListener('mousemove', function (e) {
      mx = e.clientX;
      my = e.clientY;
      cursor.classList.add('alz-cursor--visible');
    }, { passive: true });

    document.addEventListener('mouseleave', function () {
      cursor.classList.remove('alz-cursor--visible');
    });

    var interactives = 'a, button, .alz-btn, .alz-transparency__card, .alz-ritual__step, input, label, .alz-link';
    document.querySelectorAll(interactives).forEach(function (el) {
      el.addEventListener('mouseenter', function () { cursor.classList.add('alz-cursor--hover'); });
      el.addEventListener('mouseleave', function () { cursor.classList.remove('alz-cursor--hover'); });
    });

    function lerp(a, b, t) { return a + (b - a) * t; }
    (function tick() {
      cx = lerp(cx, mx, 0.1);
      cy = lerp(cy, my, 0.1);
      cursor.style.left = cx + 'px';
      cursor.style.top  = cy + 'px';
      requestAnimationFrame(tick);
    }());
  }

  /* -------------------------------------------------------------------------
     5. SPLIT MOTS -- inject spans pour animation word-by-word
     Retourne NodeList des .alz-word__inner
  --------------------------------------------------------------------------- */
  /* Trois defauts corriges le 2026-08-23, tous dans cette seule fonction :

     1. SECURITE. Les mots etaient reinjectes par innerHTML apres avoir ete lus
        par textContent, donc DECODES. Liquid echappe pourtant bien les reglages
        du titre : cette ligne annulait sa protection et ressuscitait en HTML
        vivant ce que le serveur avait neutralise. On ne construit plus de
        chaine HTML du tout, on cree des elements et on ecrit par textContent.
     2. TYPOGRAPHIE. el.textContent aplatissait le titre : les spans qui portent
        le mot en capitales et le mot en italique disparaissaient. Depuis qu'un
        filet peut sauter la cascade, la meme page rendait le titre tantot avec
        sa typographie, tantot a plat, au hasard du temps de chargement. On
        decoupe maintenant a l'interieur de chaque span, qui reste en place.
     3. ACCESSIBILITE. Chaque mot porte aria-hidden : apres decoupage, le titre
        n'avait plus aucun nom pour un lecteur d'ecran. Le texte d'origine est
        desormais pose en aria-label sur l'element. */
  function splitWords(el) {
    var texteComplet = el.textContent.replace(/\s+/g, ' ').trim();
    if (!texteComplet) return el.querySelectorAll('.alz-word__inner');

    if (!el.getAttribute('aria-label')) {
      el.setAttribute('aria-label', texteComplet);
    }

    /* Les enfants directs portent la typographie (capitales, italique,
       complement). S'il n'y en a pas, on decoupe l'element lui-meme. */
    var porteurs = el.children.length ? Array.prototype.slice.call(el.children) : [el];

    porteurs.forEach(function (porteur) {
      var mots = porteur.textContent.replace(/\s+/g, ' ').trim().split(' ');
      if (!mots[0]) return;

      var fragment = document.createDocumentFragment();
      mots.forEach(function (mot, i) {
        if (i > 0) fragment.appendChild(document.createTextNode(' '));
        var exterieur = document.createElement('span');
        exterieur.className = 'alz-word';
        exterieur.setAttribute('aria-hidden', 'true');
        var interieur = document.createElement('span');
        interieur.className = 'alz-word__inner';
        interieur.textContent = mot;
        exterieur.appendChild(interieur);
        fragment.appendChild(exterieur);
      });

      porteur.textContent = '';
      porteur.appendChild(fragment);
    });

    return el.querySelectorAll('.alz-word__inner');
  }

  /* -------------------------------------------------------------------------
     6. HERO
     - slow-zoom image (desktop + mobile)
     - clip-path wipe vertical sur le media au chargement
     - formes SVG parallax (desktop > 750px)
     - reveal titre par mots, eyebrow, sous-texte, CTA en cascade
  --------------------------------------------------------------------------- */
  function initHero() {
    var hero = document.querySelector('.alz-hero');
    if (!hero) return;

    /* Slow-zoom : cible TOUS les media du hero, pas un seul.

       Avant le 2026-08-23 la regle etait "la video si elle existe, sinon
       l'image". Depuis que la video ne se charge plus sur telephone, cette
       priorite se retournait contre nous : GSAP animait sans fin un element
       reste invisible (opacity 0 tant que sa premiere image n'est pas jouee)
       pendant que le poster, seul media reellement affiche, restait fige.
       Du calcul depense pour rien, exactement ce que le correctif de vitesse
       cherchait a supprimer. Image et video sont superposees et cadrees a
       l'identique : les animer ensemble donne le meme mouvement dans les deux
       cas, sans avoir a deviner laquelle est visible. */
    var heroMedia = hero.querySelectorAll('.alz-hero__media-el');
    var eyebrow = hero.querySelector('.alz-hero__eyebrow');
    var heading = hero.querySelector('.alz-hero__heading');
    var sub = hero.querySelector('.alz-hero__sub');
    var cta = hero.querySelector('.alz-hero__cta');
    var decoCircle = hero.querySelector('.alz-hero__deco--circle-or');
    var decoArc = hero.querySelector('.alz-hero__deco--arc-vert');

    /* Slow-zoom (actif mobile ET desktop).

       repeat: -1 tourne sans fin : sans borne, ce mouvement decoratif garde le
       moteur d'animation eveille meme quand le hero est sorti de l'ecran depuis
       dix sections -- batterie sur telephone, et aucun etat stable pour une
       capture ou un test visuel, la page ne se figeant jamais. Le ScrollTrigger
       le met en pause des qu'il n'est plus a l'ecran et le reprend au retour. */
    if (heroMedia.length) {
      gsap.fromTo(heroMedia,
        { scale: 1.0 },
        {
          scale: 1.07, duration: 18, ease: 'sine.inOut', yoyo: true, repeat: -1,
          transformOrigin: 'center center',
          scrollTrigger: {
            trigger: hero,
            start: 'top bottom',
            end: 'bottom top',
            toggleActions: 'play pause resume pause'
          }
        }
      );
    }

    /* Voile d'ouverture sur le media du hero : RETIRE le 2026-08-23.

       Ce voile etait pilote par GSAP, qui coute a lui seul 850 ms de calcul
       sur un telephone. Tant qu'il n'avait pas tourne, l'image du hero restait
       masquee -- et comme c'est elle le plus grand rendu de la page, Google
       datait celui-ci a 5 s alors que l'image etait telechargee en 1 s.
       Le hero ne depend donc plus d'aucun script pour s'afficher. Le
       slow-zoom ci-dessus reste : il ne cache rien, il ne fait que respirer. */

    /* Cascade reveals contenu

       PERFORMANCE (2026-08-23) : cette cascade durait pres de 3 s. Comme GSAP
       met le texte a opacity 0 avant de l'animer, Google datait le plus grand
       rendu de la page a la FIN de la cascade : LCP 5,6 s pour un texte deja
       present dans le HTML des la premiere seconde. Rien ne manquait, tout
       arrivait trop tard.
       Les durees du hero sont donc dissociees de celles du reste du site
       (T.* reste inchange ailleurs) : meme mouvement, deux fois et demie plus
       court. Le sous-texte, qui porte le LCP, est desormais lu vers 1,2 s. */
    var H = {
      eyebrow: 0.45,
      mots:    0.60,
      sub:     0.50,
      cta:     0.45,
      preuves: 0.45,
      stagger: 0.05
    };

    /* Filet de securite (2026-08-23) -- la cascade CACHE avant de reveler.
       Sur un telephone lent, GSAP arrive parfois plus d'une seconde apres le
       premier affichage : le hero, deja lu par la visiteuse, disparaissait
       alors pour revenir en fondu. Mesure a l'appui, ce va-et-vient reportait
       le plus grand rendu de 0,9 s a 5,5 s selon la charge de la machine.
       Passe ce delai, on renonce a l'animation d'entree : le hero reste
       simplement affiche. Le mouvement est un bonus, jamais une condition
       pour voir la page.
       Un `return` serait pratique ici mais il sauterait aussi le parallax des
       formes decoratives, plus bas dans cette meme fonction : le hero perdrait
       son relief au defilement sur les machines lentes, justement celles ou il
       est deja le plus pauvre. On saute donc la cascade, et elle seule. */
    var TROP_TARD = 1200;
    var depuisOuverture = (window.performance && performance.now) ? performance.now() : 0;
    var cascadeAtemps = depuisOuverture <= TROP_TARD;

    var tl = cascadeAtemps ? gsap.timeline({ delay: 0.05 }) : null;

    if (cascadeAtemps && eyebrow) {
      gsap.set(eyebrow, { opacity: 0, y: 14 });
      tl.to(eyebrow, { opacity: 1, y: 0, duration: H.eyebrow, ease: T.ease1 });
    }

    if (cascadeAtemps && heading) {
      var words = splitWords(heading);
      gsap.set(words, { opacity: 0, y: '110%' });
      tl.to(words, { opacity: 1, y: '0%', duration: H.mots, ease: T.ease2, stagger: H.stagger }, '-=0.28');
    }

    if (cascadeAtemps && sub) {
      gsap.set(sub, { opacity: 0, y: 22 });
      tl.to(sub, { opacity: 1, y: 0, duration: H.sub, ease: T.ease1 }, '-=0.34');
    }

    if (cascadeAtemps && cta) {
      gsap.set(cta, { opacity: 0, y: 16 });
      tl.to(cta, { opacity: 1, y: 0, duration: H.cta, ease: T.ease1 }, '-=0.30');
    }

    /* Lien secondaire et preuves factuelles : ajoutes au hero le 2026-08-23.
       Sans cette entree ils apparaissaient AVANT le titre, ce qui donnait une
       page a moitie montee pendant une seconde. */
    var ctaSecondaire = hero.querySelector('.alz-hero__cta-secondaire');
    var preuves = hero.querySelectorAll('.alz-hero__preuves li');

    if (cascadeAtemps && ctaSecondaire) {
      gsap.set(ctaSecondaire, { opacity: 0, y: 12 });
      tl.to(ctaSecondaire, { opacity: 1, y: 0, duration: H.cta, ease: T.ease1 }, '-=0.34');
    }

    if (cascadeAtemps && preuves.length) {
      gsap.set(preuves, { opacity: 0, y: 10 });
      tl.to(preuves, { opacity: 1, y: 0, duration: H.preuves, ease: T.ease1, stagger: H.stagger }, '-=0.30');
    }

    /* Formes deco : parallax multi-vitesse desktop uniquement */
    if (window.matchMedia('(min-width: 750px)').matches) {
      if (decoCircle) {
        gsap.to(decoCircle, {
          y: -70,
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 1.8 }
        });
      }
      if (decoArc) {
        gsap.to(decoArc, {
          y: 45,
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 2.5 }
        });
      }
    }
  }

  /* -------------------------------------------------------------------------
     7. REVEALS GENERIQUES -- .alz-reveal-fade et .alz-reveal-img
     Active mobile + desktop
  --------------------------------------------------------------------------- */
  function initRevealsFade() {
    document.querySelectorAll('.alz-reveal-fade').forEach(function (el) {
      gsap.fromTo(el,
        { opacity: 0, y: 32 },
        {
          opacity: 1, y: 0, duration: T.slow, ease: T.ease1,
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        }
      );
    });

    document.querySelectorAll('.alz-reveal-img').forEach(function (el) {
      gsap.fromTo(el,
        { clipPath: 'inset(0 0 100% 0)', opacity: 0 },
        {
          clipPath: 'inset(0 0 0% 0)', opacity: 1, duration: T.xslow, ease: T.ease1,
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
        }
      );
    });
  }

  /* -------------------------------------------------------------------------
     8. H2 SPLIT PAR MOTS -- .alz-split-h2
  --------------------------------------------------------------------------- */
  function initSplitHeadings() {
    document.querySelectorAll('.alz-split-h2').forEach(function (heading) {
      var words = splitWords(heading);
      gsap.fromTo(words,
        { opacity: 0, y: '110%' },
        {
          opacity: 1, y: '0%', duration: T.xslow, ease: T.ease2, stagger: T.stagger,
          scrollTrigger: { trigger: heading, start: 'top 86%', toggleActions: 'play none none none' }
        }
      );
    });
  }

  /* -------------------------------------------------------------------------
     9. TRUST BAR -- stagger reveal (actif mobile + desktop)
  --------------------------------------------------------------------------- */
  function initTrustBar() {
    var items = document.querySelectorAll('.alz-trust__item');
    if (!items.length) return;

    var trigger = (items[0].closest && items[0].closest('.alz-trust')) || items[0];
    gsap.fromTo(items,
      { opacity: 0, y: 22 },
      {
        opacity: 1, y: 0, duration: T.mid, ease: T.ease1, stagger: T.stagger,
        scrollTrigger: { trigger: trigger, start: 'top 90%', toggleActions: 'play none none none' }
      }
    );
  }

  /* -------------------------------------------------------------------------
     10. RITUAL -- SCROLL PIN + SCRUB >= 750px / stagger simple < 750px
  --------------------------------------------------------------------------- */
  function initRitual() {
    var ritual = document.querySelector('.alz-ritual');
    if (!ritual) return;

    var steps = Array.prototype.slice.call(ritual.querySelectorAll('.alz-ritual__step'));
    var header = ritual.querySelector('.alz-ritual__header');
    if (!steps.length) return;

    /* Header reveal : toujours actif */
    if (header) {
      gsap.fromTo(header,
        { opacity: 0, y: 28 },
        {
          opacity: 1, y: 0, duration: T.slow, ease: T.ease1,
          scrollTrigger: { trigger: header, start: 'top 85%', toggleActions: 'play none none none' }
        }
      );
    }

    if (false) {
      /* --- MODE SCROLL-DRIVEN PIN (DESACTIVE : creait un voile blanc + piege de scroll, rejete Samuel 11/06) --- */
      var stepsList = ritual.querySelector('.alz-ritual__steps');
      if (stepsList) stepsList.classList.add('alz-ritual__steps--vertical');
      ritual.classList.add('alz-ritual--pinned');

      /* Etat initial : step 0 visible, autres en retrait */
      steps.forEach(function (step, i) {
        if (i === 0) {
          step.classList.add('alz-ritual__step--active');
        } else {
          step.classList.add('alz-ritual__step--inactive');
          gsap.set(step, { opacity: 0.28, x: 20 });
        }
      });

      var totalScrollLen = window.innerHeight * (steps.length + 0.8);

      ScrollTrigger.create({
        trigger: ritual,
        start: 'top top',
        end: '+=' + totalScrollLen,
        pin: true,
        anticipatePin: 1,
        onUpdate: function (self) {
          var progress = Math.max(0, Math.min(1, self.progress));
          var activeIndex = Math.min(Math.floor(progress * steps.length), steps.length - 1);

          steps.forEach(function (step, i) {
            var isActive = (i === activeIndex);
            var wasActive = step.classList.contains('alz-ritual__step--active');

            if (isActive && !wasActive) {
              step.classList.remove('alz-ritual__step--inactive');
              step.classList.add('alz-ritual__step--active');
              gsap.to(step, { opacity: 1, x: 0, duration: 0.55, ease: 'power2.out' });
            } else if (!isActive && wasActive) {
              step.classList.remove('alz-ritual__step--active');
              step.classList.add('alz-ritual__step--inactive');
              gsap.to(step, { opacity: 0.28, duration: 0.35, ease: 'power1.in' });
            }
          });
        }
      });

    } else {
      /* --- MOBILE : stagger reveal simple (actif et visible) --- */
      steps.forEach(function (step, i) {
        gsap.fromTo(step,
          { opacity: 0, y: 28 },
          {
            opacity: 1, y: 0, duration: T.mid, ease: T.ease1, delay: i * 0.1,
            scrollTrigger: { trigger: step, start: 'top 88%', toggleActions: 'play none none none' }
          }
        );
      });
    }
  }

  /* -------------------------------------------------------------------------
     11. TRANSPARENCY CARDS -- stagger reveal + trait or scaleX
  --------------------------------------------------------------------------- */
  function initTransparencyCards() {
    var cards = document.querySelectorAll('.alz-transparency__card');
    if (!cards.length) return;

    var gridEl = (cards[0].closest && cards[0].closest('.alz-transparency')) || cards[0];

    gsap.fromTo(cards,
      { opacity: 0, y: 38 },
      {
        opacity: 1, y: 0, duration: T.slow, ease: T.ease1, stagger: T.staggerMd,
        scrollTrigger: { trigger: gridEl, start: 'top 82%', toggleActions: 'play none none none' }
      }
    );

    /* Trait or scaleX */
    var orRules = document.querySelectorAll('.alz-transparency__card .alz-rule-or--sm');
    if (orRules.length) {
      gsap.fromTo(orRules,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1, duration: T.mid, ease: T.ease2, stagger: T.staggerMd,
          scrollTrigger: { trigger: gridEl, start: 'top 82%', toggleActions: 'play none none none' }
        }
      );
    }
  }

  /* -------------------------------------------------------------------------
     12. AVATAR -- clip-path image + cascade contenu (mobile + desktop)
  --------------------------------------------------------------------------- */
  function initAvatar() {
    var avatar = document.querySelector('.alz-avatar');
    if (!avatar) return;

    var imgWrap = avatar.querySelector('.alz-avatar__image-fallback') || avatar.querySelector('.alz-avatar__image');
    var eyebrow = avatar.querySelector('.alz-eyebrow');
    var heading = avatar.querySelector('.alz-avatar__heading');
    var body = avatar.querySelector('.alz-avatar__body');
    var cta = avatar.querySelector('.alz-avatar__cta');

    var tl = gsap.timeline({
      scrollTrigger: { trigger: avatar, start: 'top 80%', toggleActions: 'play none none none' }
    });

    if (imgWrap) {
      tl.fromTo(imgWrap,
        { clipPath: 'inset(0 0 100% 0)', opacity: 0.9 },
        { clipPath: 'inset(0 0 0% 0)', opacity: 1, duration: T.xslow, ease: T.ease1 }
      );
    }

    if (eyebrow) {
      gsap.set(eyebrow, { opacity: 0, x: -14 });
      tl.to(eyebrow, { opacity: 1, x: 0, duration: T.mid, ease: T.ease1 }, '-=0.9');
    }

    if (heading) {
      var words = splitWords(heading);
      gsap.set(words, { opacity: 0, y: '105%' });
      tl.to(words, { opacity: 1, y: '0%', duration: T.xslow, ease: T.ease2, stagger: T.stagger }, '-=0.7');
    }

    if (body) {
      gsap.set(body, { opacity: 0, y: 20 });
      tl.to(body, { opacity: 1, y: 0, duration: T.slow, ease: T.ease1 }, '-=0.5');
    }

    if (cta) {
      gsap.set(cta, { opacity: 0, y: 14 });
      tl.to(cta, { opacity: 1, y: 0, duration: T.mid, ease: T.ease1 }, '-=0.5');
    }
  }

  /* -------------------------------------------------------------------------
     13. PROOF COUNT-UP
     .alz-proof__number[data-target] -> anime de 0 a target
  --------------------------------------------------------------------------- */
  function initProof() {
    document.querySelectorAll('.alz-proof__number[data-target]').forEach(function (el) {
      var target = parseFloat(el.getAttribute('data-target')) || 0;
      var prefix = el.getAttribute('data-prefix') || '';
      var suffix = el.getAttribute('data-suffix') || '';
      var isFloat = (target % 1 !== 0);
      var obj = { val: 0 };

      /* Valeur finale visible par defaut */
      el.textContent = prefix + target + suffix;

      gsap.fromTo(obj,
        { val: 0 },
        {
          val: target, duration: 2.0, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none', once: true },
          onUpdate: function () {
            var v = isFloat ? obj.val.toFixed(1) : Math.round(obj.val);
            el.textContent = prefix + v + suffix;
          },
          onComplete: function () {
            el.textContent = prefix + target + suffix;
          }
        }
      );
    });

    /* Reveal items preuve */
    document.querySelectorAll('.alz-proof__item').forEach(function (el, i) {
      gsap.fromTo(el,
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0, duration: T.mid, ease: T.ease1, delay: i * T.staggerMd,
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        }
      );
    });
  }

  /* -------------------------------------------------------------------------
     14. EYEBROWS GLOBAUX -- fade-x leger (hors hero traite separement)
  --------------------------------------------------------------------------- */
  function initEyebrows() {
    var selectors = [
      '.alz-ritual .alz-eyebrow',
      '.alz-transparency .alz-eyebrow',
      '.alz-avatar .alz-eyebrow',
      '.alz-proof .alz-eyebrow',
      '.alz-marquee + * .alz-eyebrow'
    ].join(', ');

    document.querySelectorAll(selectors).forEach(function (el) {
      gsap.fromTo(el,
        { opacity: 0, x: -12 },
        {
          opacity: 1, x: 0, duration: T.mid, ease: T.ease1,
          scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
        }
      );
    });
  }

  /* -------------------------------------------------------------------------
     15. FILET OR SOUS H2 -- scaleX 0->1
  --------------------------------------------------------------------------- */
  function initH2Underlines() {
    var h2s = document.querySelectorAll(
      '.alz-ritual__heading, .alz-transparency__header .alz-h2, .alz-avatar__heading, .alz-proof__header .alz-h2'
    );
    h2s.forEach(function (h2) {
      if (h2.querySelector('.alz-h2-underline')) return; /* deja injecte */
      var line = document.createElement('span');
      line.className = 'alz-h2-underline';
      line.setAttribute('aria-hidden', 'true');
      h2.appendChild(line);
      gsap.fromTo(line,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1, duration: T.slow, ease: T.ease2,
          scrollTrigger: { trigger: h2, start: 'top 88%', toggleActions: 'play none none none' }
        }
      );
    });
  }

  /* -------------------------------------------------------------------------
     16. MARQUEE REVEAL (la piste CSS anime deja via @keyframes)
  --------------------------------------------------------------------------- */
  function initMarquee() {
    var marquee = document.querySelector('.alz-marquee');
    if (!marquee) return;
    gsap.fromTo(marquee,
      { opacity: 0 },
      {
        opacity: 1, duration: T.mid, ease: T.ease1,
        scrollTrigger: { trigger: marquee, start: 'top 95%', toggleActions: 'play none none none' }
      }
    );
  }

  /* -------------------------------------------------------------------------
     17. FEATURED-COLLECTION -- cards stagger (Dawn natif)
  --------------------------------------------------------------------------- */
  function initFeaturedCollection() {
    var cards = document.querySelectorAll('.featured-collection .card-wrapper');
    if (!cards.length) return;
    var sectionEl = (cards[0].closest && cards[0].closest('.shopify-section')) || cards[0];
    gsap.fromTo(cards,
      { opacity: 0, y: 32 },
      {
        opacity: 1, y: 0, duration: T.mid, ease: T.ease1, stagger: 0.09,
        scrollTrigger: { trigger: sectionEl, start: 'top 88%', toggleActions: 'play none none none' }
      }
    );
  }

  /* -------------------------------------------------------------------------
     18. PERSONAS -- galerie portraits, reveal en cascade avec stagger
     .alz-personas__item : fade + translateY, stagger 0.1s
     Actif mobile + desktop
  --------------------------------------------------------------------------- */
  function initPersonas() {
    var items = document.querySelectorAll('.alz-personas__item');
    if (!items.length) return;

    var sectionEl = (items[0].closest && items[0].closest('.alz-personas')) || items[0];

    gsap.fromTo(items,
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: T.slow, ease: T.ease1, stagger: 0.1,
        scrollTrigger: {
          trigger: sectionEl,
          start: 'top 82%',
          toggleActions: 'play none none none'
        }
      }
    );

    /* Legendes : reveal leger apres les portraits */
    var captions = document.querySelectorAll('.alz-personas__caption');
    if (captions.length) {
      gsap.fromTo(captions,
        { opacity: 0 },
        {
          opacity: 1, duration: T.mid, ease: T.ease1, stagger: 0.08,
          scrollTrigger: {
            trigger: sectionEl,
            start: 'top 78%',
            toggleActions: 'play none none none'
          }
        }
      );
    }
  }

  /* -------------------------------------------------------------------------
     19. INIT -- sequencement apres DOM pret
  --------------------------------------------------------------------------- */
  function init() {
    initCursor();
    initHero();
    initRevealsFade();
    initSplitHeadings();
    initTrustBar();
    initRitual();
    initTransparencyCards();
    initAvatar();
    initProof();
    initEyebrows();
    initH2Underlines();
    initMarquee();
    initFeaturedCollection();
    initPersonas();

    /* ScrollTrigger.refresh() apres chargement total (fonts, images) */
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* -------------------------------------------------------------------------
     19. SHOPIFY DESIGN MODE -- reinit ScrollTrigger
  --------------------------------------------------------------------------- */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      ScrollTrigger.refresh();
    });
  }

}());
